/**
 * Local-echo store (file-handling §13 V1, §14.3.6).
 *
 * The uploader holds the actual bytes. Backend processing takes ~16s for an
 * image or PDF and minutes for video/audio, during which the server can only
 * honestly say "processing" with a null URL. Rule (e) says the person who just
 * picked a file expects to see it immediately — so we keep the bytes they
 * handed us and render those until the server's rendition is `ready`.
 *
 * Scope is deliberately the session (a module-level Map): a reload empties it,
 * and `useFileRef` then degrades to the honest processing state rather than to
 * a lie. Nothing is persisted; object URLs are per-document anyway.
 *
 * Keyed by the S3 upload key returned by `uploadFileToS3`, which is also what
 * the client sends to the API — so a surface that has just uploaded can look
 * up its echo without threading the File object through its component tree.
 */

export interface LocalEcho {
  /** `URL.createObjectURL(file)` — renderable immediately, revoked on clear. */
  objectURL: string;
  /** Original basename, matching `FileRef.filename`. */
  filename: string;
  /** Bytes, matching `FileRef.size`. */
  size: number;
  /** The browser's MIME type for the picked file (`File.type`, may be ''). */
  mimeType: string;
}

const echoes = new Map<string, LocalEcho>();

/**
 * Capture a just-uploaded file so surfaces can render it before the server
 * finishes processing. Re-registering the same key revokes the previous echo,
 * so a replaced file never leaks its object URL.
 */
export function registerLocalEcho(fileKey: string, file: File): LocalEcho {
  clearLocalEcho(fileKey);
  const echo: LocalEcho = {
    objectURL: URL.createObjectURL(file),
    filename: file.name,
    size: file.size,
    mimeType: file.type,
  };
  echoes.set(fileKey, echo);
  return echo;
}

/** The echo for `fileKey`, or null when there is none (e.g. after a reload). */
export function getLocalEcho(fileKey: string | null | undefined): LocalEcho | null {
  if (!fileKey) return null;
  return echoes.get(fileKey) ?? null;
}

/**
 * Drop the echo and revoke its object URL. Safe to call for an unknown key.
 *
 * Callers should clear once the server-side `FileRef` reaches a terminal state
 * and the surface has switched to the real URL — but forgetting is bounded:
 * the map dies with the document.
 */
export function clearLocalEcho(fileKey: string | null | undefined): void {
  if (!fileKey) return;
  const existing = echoes.get(fileKey);
  if (!existing) return;
  URL.revokeObjectURL(existing.objectURL);
  echoes.delete(fileKey);
}

/** Revoke and drop every echo. Used by teardown and by tests. */
export function clearAllLocalEchoes(): void {
  for (const key of Array.from(echoes.keys())) clearLocalEcho(key);
}
